import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpEvent, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseRepository } from './base.repository';
import envConfig from '../../../env-config';

export interface UploadFileResponse {
  path: string;
  fileName: string;
  sizeBytes: number;
}

/**
 * HTTP repository for Swift object storage operations via BFF → fps-api-v2 StorageController.
 *
 * Endpoints (all under /api/v1/storage):
 *   POST   /file                  — upload (multipart: file + path)
 *   GET    /file/download?path=   — download as blob
 *   DELETE /file?path=            — delete
 */
@Injectable({ providedIn: 'root' })
export class ObjectStorageRepository extends BaseRepository {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${envConfig.bffUrl ?? ''}/api/v1/storage`;

  /**
   * Upload a single file to Swift.
   * Reports progress events so the caller can show a progress bar.
   */
  uploadFile(swiftPath: string, file: File): Observable<HttpEvent<unknown>> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('path', swiftPath);

    const req = new HttpRequest('POST', `${this.baseUrl}/file`, formData, {
      reportProgress: true,
      context: this.authContext(),
    });

    return this.http.request<UploadFileResponse>(req);
  }

  /**
   * Download a file from Swift as a Blob.
   */
  getFile(swiftPath: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/file/download`, {
      params: { path: swiftPath },
      responseType: 'blob',
      context: this.authContext(),
    });
  }

  /**
   * Delete a single file from Swift.
   */
  deleteFile(swiftPath: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/file`, {
      params: { path: swiftPath },
      context: this.authContext(),
    });
  }

  /**
   * Delete an entire directory (and all objects under it) from Swift.
   * Called on wizard cancel to clean up the entire session temp folder.
   */
  deleteDirectory(dirPath: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/directory`, {
      params: { path: dirPath },
      context: this.authContext(),
    });
  }
}
